import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface TenantThemeConfig {
  tenantId: string;
  tenantName: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  logo?: string;
  favicon?: string;
}

const DEFAULT_THEME: TenantThemeConfig = {
  tenantId: 'default',
  tenantName: 'WeSportech Arena',
  primaryColor: '#0A0F2C',
  secondaryColor: '#EAEAF2',
  accentColor: '#00A6FF'
};

@Injectable({
  providedIn: 'root'
})
export class TenantThemeService {
  private currentTheme$ = new BehaviorSubject<TenantThemeConfig>(DEFAULT_THEME);

  constructor() {
    this.initializeTheme();
  }

  /**
   * Get current theme as observable
   */
  getCurrentTheme(): Observable<TenantThemeConfig> {
    return this.currentTheme$.asObservable();
  }

  /**
   * Get current theme value
   */
  getCurrentThemeValue(): TenantThemeConfig {
    return this.currentTheme$.value;
  }

  /**
   * Apply tenant theme
   */
  applyTenantTheme(config: TenantThemeConfig): void {
    this.setThemeVariables(config);
    this.currentTheme$.next(config);

    // Store in localStorage for persistence
    localStorage.setItem('tenant-theme', JSON.stringify(config));

    // Update favicon if provided
    if (config.favicon) {
      this.updateFavicon(config.favicon);
    }

    // Update page title
    document.title = `${config.tenantName} - Arena Portal`;
  }

  /**
   * Reset to default theme
   */
  resetToDefaultTheme(): void {
    this.applyTenantTheme(DEFAULT_THEME);
    localStorage.removeItem('tenant-theme');
  }

  /**
   * Initialize theme from localStorage or default
   */
  private initializeTheme(): void {
    const storedTheme = localStorage.getItem('tenant-theme');

    if (storedTheme) {
      try {
        const theme: TenantThemeConfig = JSON.parse(storedTheme);
        this.applyTenantTheme(theme);
      } catch (error) {
        console.error('Error loading stored theme:', error);
        this.applyTenantTheme(DEFAULT_THEME);
      }
    } else {
      this.applyTenantTheme(DEFAULT_THEME);
    }
  }

  /**
   * Set CSS custom properties for theming
   */
  private setThemeVariables(config: TenantThemeConfig): void {
    const root = document.documentElement;

    // Primary color variants
    root.style.setProperty('--tenant-primary-color', config.primaryColor);
    root.style.setProperty('--tenant-primary-hover', this.adjustBrightness(config.primaryColor, -10));
    root.style.setProperty('--tenant-primary-light', this.adjustBrightness(config.primaryColor, 40));
    root.style.setProperty('--tenant-primary-dark', this.adjustBrightness(config.primaryColor, -20));

    // Secondary color variants
    root.style.setProperty('--tenant-secondary-color', config.secondaryColor);
    root.style.setProperty('--tenant-secondary-hover', this.adjustBrightness(config.secondaryColor, -5));

    // Accent color variants
    root.style.setProperty('--tenant-accent-color', config.accentColor);
    root.style.setProperty('--tenant-accent-hover', this.adjustBrightness(config.accentColor, -10));
    root.style.setProperty('--tenant-accent-light', this.adjustBrightness(config.accentColor, 30));
  }

  /**
   * Adjust color brightness
   */
  private adjustBrightness(color: string, percent: number): string {
    // Remove # if present
    const hex = color.replace('#', '');

    // Convert to RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Adjust brightness
    const adjustedR = Math.max(0, Math.min(255, r + (r * percent) / 100));
    const adjustedG = Math.max(0, Math.min(255, g + (g * percent) / 100));
    const adjustedB = Math.max(0, Math.min(255, b + (b * percent) / 100));

    // Convert back to hex
    const toHex = (n: number) => {
      const hex = Math.round(n).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };

    return `#${toHex(adjustedR)}${toHex(adjustedG)}${toHex(adjustedB)}`;
  }

  /**
   * Update favicon
   */
  private updateFavicon(faviconUrl: string): void {
    const link: HTMLLinkElement = document.querySelector("link[rel*='icon']") || document.createElement('link');
    link.type = 'image/x-icon';
    link.rel = 'shortcut icon';
    link.href = faviconUrl;
    document.getElementsByTagName('head')[0].appendChild(link);
  }

  /**
   * Generate theme preview
   */
  generateThemePreview(config: Partial<TenantThemeConfig>): string {
    const primaryColor = config.primaryColor || DEFAULT_THEME.primaryColor;
    const accentColor = config.accentColor || DEFAULT_THEME.accentColor;

    return `
      <div style="display: flex; gap: 8px;">
        <div style="width: 40px; height: 40px; background: ${primaryColor}; border-radius: 4px;"></div>
        <div style="width: 40px; height: 40px; background: ${accentColor}; border-radius: 4px;"></div>
      </div>
    `;
  }
}
